/**
 * LayouterSettingsOverlay — Floating settings panel for layout parameters.
 * Second gear icon (bottom-right), opens a compact overlay with layout-specific controls.
 */
import React, { useState, useCallback } from 'react';

interface LayouterSettingsProps {
    /** gear sits inline in the page's bottom dock instead of floating (Wave 2026-09-13) */
    inline?: boolean;
    store: any;
}

const pill = (active: boolean, color: string): React.CSSProperties => ({
    flex: 1, padding: '5px 0', fontSize: '10px', fontWeight: active ? 700 : 500,
    color: active ? '#fff' : color, textAlign: 'center',
    background: active ? color : 'rgba(255,255,255,0.06)',
    border: 'none', borderRadius: '6px', cursor: 'pointer',
});

const LayouterSettingsOverlay: React.FC<LayouterSettingsProps> = ({ store, inline }) => {
    const [open, setOpen] = useState(false);

    const orientation = store((s: any) => s.layoutOrientation);
    const upMode = store((s: any) => s.layoutUpMode);
    const spacingMode = store((s: any) => s.layoutSpacingMode);
    const spacing = store((s: any) => s.layoutSpacing);
    const spiralTurns = store((s: any) => s.spiralTurns);
    const spiralGrowth = store((s: any) => s.spiralGrowthRate);
    const tileAspect = store((s: any) => s.tileAspectMode);

    const set = useCallback((action: string, value: any) => {
        const fn = store.getState()[action];
        if (fn) fn(value);
    }, [store]);

    return (
        <>
            {/* Gear button — bottom right, above shape bar */}
            <button
                onClick={() => setOpen(!open)}
                title="Layout-Einstellungen"
                style={{
                    ...(inline ? { flex: '0 0 auto' } : { position: 'fixed', bottom: '52px', right: '10px', zIndex: 10001 }),
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: open ? '#6366f1' : 'rgba(15,23,42,0.85)',
                    border: '1px solid rgba(99,102,241,0.4)',
                    color: open ? '#fff' : '#818cf8', fontSize: '16px',
                    cursor: 'pointer', backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                &#9881;
            </button>

            {open && (
                <div style={{
                    position: 'fixed', bottom: inline ? '104px' : '95px', right: '10px', zIndex: 10000,
                    width: '220px', maxHeight: '60vh', overflowY: 'auto',
                    background: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px',
                    padding: '12px', fontSize: '11px', color: '#e2e8f0',
                }}>
                    <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '10px', color: '#818cf8' }}>Layout Settings</div>

                    {/* Tile Aspect */}
                    <Label>Tile Aspect</Label>
                    <Row>
                        <button style={pill(tileAspect === 'square', '#6366f1')} onClick={() => set('setTileAspectMode', 'square')}>Square</button>
                        <button style={pill(tileAspect === 'individual', '#6366f1')} onClick={() => set('setTileAspectMode', 'individual')}>Individual</button>
                    </Row>

                    {/* Orientation */}
                    <Label>Orientation</Label>
                    <Row>
                        <button style={pill(orientation === 'aligned', '#22c55e')} onClick={() => set('setLayoutOrientation', 'aligned')}>Aligned</button>
                        <button style={pill(orientation === 'perpendicular', '#22c55e')} onClick={() => set('setLayoutOrientation', 'perpendicular')}>Perp.</button>
                    </Row>

                    {/* Up Mode */}
                    <Label>Up Vector</Label>
                    <Row>
                        <button style={pill(upMode === 'world', '#f59e0b')} onClick={() => set('setLayoutUpMode', 'world')}>World</button>
                        <button style={pill(upMode === 'path', '#f59e0b')} onClick={() => set('setLayoutUpMode', 'path')}>Path</button>
                    </Row>

                    {/* Spacing Mode */}
                    <Label>Spacing</Label>
                    <Row>
                        <button style={pill(spacingMode === 'margin', '#3b82f6')} onClick={() => set('setLayoutSpacingMode', 'margin')}>Margin</button>
                        <button style={pill(spacingMode === 'distribute', '#3b82f6')} onClick={() => set('setLayoutSpacingMode', 'distribute')}>Distribute</button>
                    </Row>

                    {/* Gap / Spacing Value */}
                    <Label>Gap <Val>{spacing.toFixed(2)}</Val></Label>
                    <input type="range" min="0" max="2" step="0.01" value={spacing}
                        onChange={e => set('setLayoutSpacing', parseFloat(e.target.value))}
                        style={{ width: '100%', accentColor: '#6366f1' }} />


                    {/* Spiral */}
                    <div style={{ borderTop: '1px solid rgba(99,102,241,0.15)', marginTop: '8px', paddingTop: '8px' }}>
                        <Label>Spiral Turns <Val>{spiralTurns.toFixed(1)}</Val></Label>
                        <input type="range" min="0.5" max="8" step="0.5" value={spiralTurns}
                            onChange={e => set('setSpiralTurns', parseFloat(e.target.value))}
                            style={{ width: '100%', accentColor: '#a855f7' }} />

                        <Label>Spiral Growth <Val>{spiralGrowth.toFixed(1)}</Val></Label>
                        <input type="range" min="0.5" max="4" step="0.1" value={spiralGrowth}
                            onChange={e => set('setSpiralGrowthRate', parseFloat(e.target.value))}
                            style={{ width: '100%', accentColor: '#a855f7' }} />
                    </div>
                </div>
            )}
        </>
    );
};

// Helper components
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '6px', marginBottom: '2px', display: 'flex', justifyContent: 'space-between' }}>{children}</div>
);
const Val: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span style={{ color: '#818cf8', fontFamily: 'monospace' }}>{children}</span>
);
const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>{children}</div>
);

export default LayouterSettingsOverlay;
